import { useCallback, useEffect, useMemo } from 'react';

import { SPLASH_SCREEN_FAILSAFE_TIMEOUT } from 'App';
import { usePathname } from 'expo-router';
import { useStoreContext } from 'features/store';
import { useStoreDiagnostics } from 'features/store/hooks/use-store-diagnostics';

import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useAppStateChangeListener } from 'hooks/use-app-state-change-listener';
import { routes } from 'utils/routing';

import { shouldSkipStoreDialogLogic } from '../utils/store-dialog-conditions';

import { useDialogActions } from './use-dialog-actions';
import { useIsStoreUnavailable } from './use-is-store-unavailable';
import { useRestaurantSearch } from './use-restaurant-search';

const EXCLUDED_ROUTES = [routes.storeLocator, routes.deliveryAddress, routes.store];

export const useStoreOrderUnavailableDialog = () => {
  const { addAppStateChangeListeners, removeAppStateChangeListeners } = useAppStateChangeListener();
  const { noStoreSelected, isSelectingStore, store } = useStoreContext();
  const pathname = usePathname();
  const isCurrentPathExcluded = useMemo(
    () => EXCLUDED_ROUTES.some(route => pathname.startsWith(route)),
    [pathname]
  );
  const storeUnavailableData = useIsStoreUnavailable();
  const getStoreDiagnostics = useStoreDiagnostics();

  const {
    isRestaurantSearchLoading,
    availableRestaurantForDelivery,
    availableRestaurantForPickUp,
    isDeliverySearchComplete,
    isDelivery,
    executeSearch,
    resetSearchState,
  } = useRestaurantSearch();

  const { isDialogOpen, selectNearbyStore, chooseAnotherStore, setIsDialogOpen } = useDialogActions(
    {
      availableRestaurant:
        availableRestaurantForDelivery ?? availableRestaurantForPickUp ?? undefined,
      clearAllSearchState: resetSearchState,
    }
  );

  const shouldSkipLogic = shouldSkipStoreDialogLogic({
    isStoreUnavailable: storeUnavailableData.isStoreUnavailable,
    isCurrentPathExcluded,
    isDialogOpen,
    isSelectingStore,
    isRestaurantSearchLoading,
    noStoreSelected,
  });

  useLogUserErrorMessage({
    message: "Store not available: Store can't be used for ordering",
    logWhenTrue: isDialogOpen,
    severity: LogUserErrorSeverity.Informational,
    context: { ...getStoreDiagnostics(store), source: 'storeUnavailableModal' },
  });

  const handleDialogClose = useCallback(() => {
    setIsDialogOpen(false);
    resetSearchState();
  }, [resetSearchState, setIsDialogOpen]);

  useEffect(() => {
    if (shouldSkipLogic) {
      return;
    }

    executeSearch();
  }, [shouldSkipLogic, executeSearch]);

  // Handle dialog state
  useEffect(() => {
    if (shouldSkipLogic) {
      return;
    }

    // For delivery orders, check if we have results and if we have a restaurant available automatically select it
    // If we don't have a restaurant available after search completes, show the dialog
    if (isDelivery) {
      if (isDeliverySearchComplete) {
        if (availableRestaurantForDelivery?._id) {
          selectNearbyStore();
          return;
        }
        setIsDialogOpen(true);
        return;
      }
      return;
    }

    // For pickup orders - always show dialog after search completes
    setIsDialogOpen(true);
  }, [
    shouldSkipLogic,
    isRestaurantSearchLoading,
    isDelivery,
    isDeliverySearchComplete,
    availableRestaurantForDelivery,
    selectNearbyStore,
    setIsDialogOpen,
  ]);

  useEffect(() => {
    if (shouldSkipLogic || isDialogOpen) {
      return;
    }

    // Prevent show the dialog while the splash screen is visible
    const timeoutId = setTimeout(() => {
      if (!isDialogOpen) {
        setIsDialogOpen(true);
      }
    }, SPLASH_SCREEN_FAILSAFE_TIMEOUT);

    return () => clearTimeout(timeoutId);
  }, [shouldSkipLogic, isDialogOpen, isRestaurantSearchLoading, setIsDialogOpen]);

  useEffect(() => {
    if (
      (isDialogOpen && isCurrentPathExcluded) ||
      !storeUnavailableData.shouldShowUnavailableDialog
    ) {
      handleDialogClose();
    }
  }, [
    isDialogOpen,
    isCurrentPathExcluded,
    handleDialogClose,
    storeUnavailableData.shouldShowUnavailableDialog,
  ]);

  useEffect(() => {
    addAppStateChangeListeners(handleDialogClose);
    return () => {
      removeAppStateChangeListeners();
    };
  }, [addAppStateChangeListeners, removeAppStateChangeListeners, handleDialogClose]);

  return {
    isStoreUnavailable: storeUnavailableData,
    isDialogOpen,
    isDelivery,
    selectNearbyStore,
    chooseAnotherStore,
    setIsDialogOpen,
    isRestaurantSearchLoading,
    availableRestaurantForDelivery,
    availableRestaurantForPickUp,
  };
};
